import { AvailabilityResponse } from "../src/api/trpc/time-finder";
import { TimeFinderInstanceEntity } from "../src/entity/TimeFinderInstance";
import { PollType } from "./sesh_types";

// Only add at the end of this, and do not remove
export enum LogType {
	EventCreate,
	EventEdit,
	EventAddRSVP,
	EventRemoveRSVP,
	EventDelete,
	PollCreate,
	PollEdit,
	PollAddOption,
	PollAddVote,
	PollRemoveVote,
	PollForceEnd,
	PollDelete,
	SettingsModify,
	SettingsPremiumModify,
	EventTemplateFavorite,
	EventTemplateUnfavorite,
	TimeFinderCreate,
	TimeFinderEdit,
	TimeFinderVote,
	TimeFinderDelete,
}

export type BaseEventLog = {
	title: string;
	event_id: string;
};

export type BaseEventTemplateLog = {
	title: string;
	id: string;
};

export type EventRSVPLog = BaseEventLog & {
	initiator_id?: string;
	option_name: string;
	option_emoji: string;
	option_is_custom: boolean;
};

export type BasePollLog = {
	title: string;
	poll_id: string;
	poll_type: PollType;
};

type PollVoteLog = BasePollLog & { option_name: string; is_anonymous: boolean };

export type BaseTimeFinderLog = {
	title: string;
	time_finder_id: string;
};

export type TimeFinderVoteLog = BaseTimeFinderLog & {
	user: Omit<TimeFinderInstanceEntity["users"][0], "availability"> & {
		availability: {
			id: string;
			status: AvailabilityResponse;
			date_ms: number;
		}[];
	};
	are_times_specified: boolean;
	is_anonymous: boolean;
};

// If this has a type error it likely means we're missing an enum in LogType
export type LogData = {
	[T in LogType]: {
		[LogType.EventCreate]: BaseEventLog;
		[LogType.EventEdit]: BaseEventLog;
		[LogType.EventAddRSVP]: EventRSVPLog;
		[LogType.EventRemoveRSVP]: EventRSVPLog;
		[LogType.EventDelete]: BaseEventLog;
		[LogType.PollCreate]: BasePollLog;
		[LogType.PollEdit]: BasePollLog;
		[LogType.PollAddOption]: BasePollLog & { option_name: string };
		[LogType.PollDelete]: BasePollLog;
		[LogType.PollForceEnd]: BasePollLog;
		[LogType.PollAddVote]: PollVoteLog;
		[LogType.PollRemoveVote]: PollVoteLog;
		[LogType.SettingsModify]: {
			setting?: string;
		};
		[LogType.SettingsPremiumModify]: {
			is_activate: boolean;
		};
		[LogType.EventTemplateFavorite]: BaseEventTemplateLog;
		[LogType.EventTemplateUnfavorite]: BaseEventTemplateLog;
		[LogType.TimeFinderCreate]: BaseTimeFinderLog;
		[LogType.TimeFinderEdit]: BaseTimeFinderLog;
		[LogType.TimeFinderVote]: TimeFinderVoteLog;
		[LogType.TimeFinderDelete]: BaseTimeFinderLog;
	}[T];
};

export const LogTypeGroups = {
	Event: [
		LogType.EventAddRSVP,
		LogType.EventCreate,
		LogType.EventDelete,
		LogType.EventEdit,
		LogType.EventRemoveRSVP,
		LogType.EventTemplateFavorite,
		LogType.EventTemplateUnfavorite,
	],
	Poll: [
		LogType.PollCreate,
		LogType.PollDelete,
		LogType.PollEdit,
		LogType.PollForceEnd,
		LogType.PollAddVote,
		LogType.PollRemoveVote,
		LogType.PollAddOption,
	],
	Settings: [LogType.SettingsModify, LogType.SettingsPremiumModify],
	TimeFinder: [
		LogType.TimeFinderCreate,
		LogType.TimeFinderEdit,
		LogType.TimeFinderVote,
		LogType.TimeFinderDelete,
	],
} satisfies Record<string, LogType[]>;
